import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of, switchMap, throwError, merge, EMPTY } from 'rxjs';
import { IndexedDBService } from '@libs/shared-services';
import { patientImagesDBKeys } from '@libs/utils';

export interface PatientImageData {
  image: Blob;
  createdAt: Date;
  contentType?: string | null;
}

@Injectable({ providedIn: 'root' })
export class PatientImageInterceptorService implements HttpInterceptor {
  private readonly indexedDbService = inject(IndexedDBService);

  private db: IDBDatabase | undefined;

  constructor() {
    this.indexedDbService
      .dbOpen(patientImagesDBKeys.dbName, patientImagesDBKeys.version, patientImagesDBKeys.objectStoreName)
      .subscribe((db) => {
        this.db = db;
      });
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<Blob>> {
    if (req.url.includes('images/') && this.db) {
      const imageId = req.url.match(/images\/(.+)/)?.[1];
      if (imageId) {
        return merge(
          this.getCachedImage(imageId).pipe(
            catchError((error) => {
              return EMPTY;
            }),
          ),
          this.handleRequestAndStoreInCache(imageId, next, req),
        );
      }
    }
    return next.handle(req);
  }

  private readonly getCachedImage = (imageId: string, isShowcase: boolean = false) => {
    const readOS = this.db!.transaction([patientImagesDBKeys.objectStoreName], 'readonly').objectStore(patientImagesDBKeys.objectStoreName);
    return this.indexedDbService.dbGet<PatientImageData>(readOS, imageId).pipe(
      switchMap((data) => {
        if (data) {
          return of(
            new HttpResponse({
              body: data.image,
              headers: new HttpHeaders({
                'content-type': data.contentType ?? '',
              }),
              status: 200,
            }),
          );
        }
        return EMPTY;
      }),
    );
  };

  private readonly handleRequestAndStoreInCache = (
    imageId: string,
    next: HttpHandler,
    req: HttpRequest<unknown>,
  ): Observable<HttpEvent<Blob>> => {
    return next.handle(req).pipe(
      catchError(() => {
        return throwError(() => 'Error fetching image');
      }),
      switchMap((response) => {
        if (response instanceof HttpResponse) {
          const writeOs = this.db!.transaction([patientImagesDBKeys.objectStoreName], 'readwrite').objectStore(
            patientImagesDBKeys.objectStoreName,
          );
          const isShowcase = req.url.includes('showcase');
          const contentType = isShowcase ? 'image/jpeg' : response.headers.get('content-type');
          const headers = contentType ? response.headers.set('content-type', contentType) : response.headers;

          return this.indexedDbService
            .dbPut<PatientImageData>(writeOs, imageId, {
              image: response.body,
              contentType,
              createdAt: new Date(),
            })
            .pipe(
              switchMap(async () => {
                await this.indexedDbService.dbHouseKeeping(writeOs);
                return response.clone({ headers });
              }),
            );
        }
        return of(response);
      }),
    );
  };
}
